import React, { useEffect, useState } from "react";
import { Sparkles as SparklesIcon, Crown, Heart, Cake, Star } from "lucide-react";
import Sparkles from "./Sparkles";
import { fileUrl } from "../lib/api";
import { BUILDUP_MESSAGES, formatEventDate, formatEventTime } from "../lib/director";
import { PRE_MESSAGES, CAKE_PREP_MESSAGES, ARRIVAL_HEADERS, ARRIVAL_DURATION, ANTICIPATION_MESSAGES } from "../lib/directorFlow";
import Memories from "../pages/Memories";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };
const CASTLE_BG =
  "https://images.unsplash.com/photo-1766191395451-91c79c6ed395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxtYWdpY2FsJTIwZmFpcnklMjB0YWxlJTIwY2FzdGxlJTIwcGluayUyMHNreXxlbnwwfHx8fDE3ODE1OTQ1OTd8MA&ixlib=rb-4.1.0&q=85";

/* ============================================================
   PRE-EVENT — Royal Doors with countdown + rotating messages
============================================================ */
export function PreEventScene({ lang, startsAt, forceOpen = false }) {
  const [now, setNow] = useState(Date.now());
  const [msgIdx, setMsgIdx] = useState(0);
  const [opening, setOpening] = useState(forceOpen);

  useEffect(() => {
    const tm = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tm);
  }, []);

  useEffect(() => {
    const tm = setInterval(() => setMsgIdx((i) => (i + 1) % PRE_MESSAGES.length), 6000);
    return () => clearInterval(tm);
  }, []);

  const diff = startsAt ? Math.max(0, new Date(startsAt).getTime() - now) : 0;
  // When start time arrives, play the door-opening animation once before
  // the director transitions away. Triggered at <2 sec to start, or immediately
  // when forceOpen=true (admin pressed Start The Show).
  useEffect(() => {
    if (forceOpen) { setOpening(true); return; }
    if (diff > 0 && diff < 2000 && !opening) setOpening(true);
  }, [diff, opening, forceOpen]);

  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const labels = lang === "te"
    ? { d: "రోజులు", h: "గంటలు", m: "నిమిషాలు", s: "క్షణాలు" }
    : { d: "Days", h: "Hours", m: "Minutes", s: "Seconds" };
  const msg = PRE_MESSAGES[msgIdx];

  return (
    <div className="absolute inset-0 overflow-hidden grid place-items-center" data-testid="director-pre">
      {/* Night sky backdrop */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse at 50% 20%, #4a1654 0%, #1d0832 50%, #08041a 100%)",
      }} />
      <Sparkles count={70} />

      {/* Animated castle doors (CSS only) */}
      <div className="absolute inset-x-0 bottom-0 grid place-items-end pointer-events-none" style={{ height: "55%" }}>
        <div className="relative" style={{ width: "min(70vw, 600px)", aspectRatio: "1/1.1" }}>
          {/* Castle outline */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 660'><defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='%23311054'/><stop offset='1' stop-color='%2316072a'/></linearGradient></defs><path fill='url(%23g)' stroke='%23D4AF37' stroke-width='2.5' d='M120 660 V340 H180 V300 H220 V240 H260 V200 L300 160 L340 200 V240 H380 V300 H420 V340 H480 V660 Z'/><circle cx='220' cy='150' r='6' fill='%23FFE89C'/><circle cx='300' cy='100' r='8' fill='%23FFE89C'/><circle cx='380' cy='150' r='6' fill='%23FFE89C'/></svg>\") no-repeat center bottom",
              backgroundSize: "100% 100%",
              filter: "drop-shadow(0 0 40px rgba(212,175,55,0.35))",
            }}
          />
          {/* Door cavity */}
          <div
            className="absolute"
            style={{
              left: "37%", bottom: "0%", width: "26%", height: "38%",
              background: "linear-gradient(180deg, #4a1654 0%, #1c0530 100%)",
              borderTopLeftRadius: "100%",
              borderTopRightRadius: "100%",
              border: "1.5px solid #D4AF37",
              boxShadow: "inset 0 0 30px rgba(0,0,0,0.7)",
              overflow: "hidden",
            }}
          >
            {/* Left door panel */}
            <div
              className="absolute top-0 bottom-0"
              style={{
                left: 0, width: "50%",
                background: "linear-gradient(180deg, #6f3a1a 0%, #3c1d0a 100%)",
                borderTopLeftRadius: "100%",
                borderRight: "1px solid #D4AF37",
                transformOrigin: "left center",
                transform: opening ? "perspective(800px) rotateY(-90deg)" : "rotateY(0)",
                transition: "transform 1.5s ease-in",
              }}
            />
            {/* Right door panel */}
            <div
              className="absolute top-0 bottom-0"
              style={{
                right: 0, width: "50%",
                background: "linear-gradient(180deg, #6f3a1a 0%, #3c1d0a 100%)",
                borderTopRightRadius: "100%",
                borderLeft: "1px solid #D4AF37",
                transformOrigin: "right center",
                transform: opening ? "perspective(800px) rotateY(90deg)" : "rotateY(0)",
                transition: "transform 1.5s ease-in",
              }}
            />
          </div>
        </div>
      </div>

      {/* Foreground — title + countdown + message */}
      <div className="relative z-10 flex flex-col items-center text-center px-6" style={{ marginBottom: "30vh" }}>
        <Crown className="text-[#FCF6BA] mb-4" size={56} />
        <p className="gold-text-soft font-script drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(40px, 7vw, 88px)" }}>
          {lang === "te" ? "అనఘ రాజ్యం" : "Anagha's Kingdom"}
        </p>
        {startsAt && (
          <div className="glass-deep rounded-full px-6 py-2 mt-4 inline-flex items-center gap-2" data-testid="pre-saved-schedule">
            <SparklesIcon size={14} className="text-[#FCF6BA]" />
            <p className="font-script gold-text-soft" style={{ ...TE, fontSize: "clamp(15px, 2.2vw, 20px)" }}>
              {formatEventDate(startsAt, lang)} · {formatEventTime(startsAt, lang)}
            </p>
          </div>
        )}

        <div className="mt-7 flex items-end gap-4 sm:gap-7 text-blush/95" data-testid="pre-countdown">
          {[[d, labels.d], [h, labels.h], [m, labels.m], [s, labels.s]].map(([val, label], i) => (
            <div key={i} className="flex flex-col items-center">
              <span className="gold-text-soft font-display font-semibold leading-none tabular-nums" style={{ fontSize: "clamp(38px, 6vw, 80px)" }}>
                {String(val).padStart(2, "0")}
              </span>
              <span className="label-eyebrow mt-2 text-blush/80" style={TE}>{label}</span>
            </div>
          ))}
        </div>

        <p
          key={msgIdx}
          className="mt-8 font-script gold-text-soft animate-[fadeUp_1s_ease-out] px-4 drop-shadow"
          style={{ ...TE, fontSize: "clamp(20px, 3.4vw, 32px)", maxWidth: "min(85vw, 800px)" }}
        >
          {lang === "te" ? msg.te : msg.en}
        </p>
      </div>
    </div>
  );
}

/* ============================================================
   ARRIVAL — "Join Live" — invites guests to scan the QR
============================================================ */
export function JoinLiveScene({ lang, guestCount }) {
  return (
    <SceneShell tone="dark" testid="director-join">
      <SparklesIcon className="text-[#FCF6BA] mb-3" size={48} />
      <p className="gold-text font-script drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(36px, 7vw, 80px)" }}>
        {lang === "te" ? "మీరూ కింగ్‌డమ్‌లో చేరండి" : "Join The Kingdom Live"}
      </p>
      <p className="font-display text-blush/90 mt-4" style={{ ...TE, fontSize: "clamp(15px, 2.6vw, 22px)" }}>
        {lang === "te"
          ? "మీ ఫోన్‌లో QR కోడ్ స్కాన్ చేసి, ఒక సెల్ఫీ అప్‌లోడ్ చేయండి"
          : "Scan the QR on your table & upload a selfie"}
      </p>
      <div className="glass-deep rounded-full px-6 py-2 mt-8 inline-flex items-center gap-2">
        <Star size={14} className="text-[#FCF6BA]" />
        <p className="label-eyebrow text-blush" style={TE}>
          {guestCount} {lang === "te" ? "మంది ఇప్పటికే చేరారు" : "have already joined"}
        </p>
      </div>
    </SceneShell>
  );
}

/* ============================================================
   STATS counter — Phase 2 slot + farewell numbers
============================================================ */
export function StatsScene({ lang, stats, photos }) {
  const items = [
    { n: stats.total || 0,    te: "మంది అతిథులు",  en: "Guests Joined" },
    { n: stats.total || 0,    te: "నక్షత్రాలు",      en: "Stars Lit" },
    { n: stats.messages || 0, te: "ఆశీర్వాదాలు",     en: "Blessings Received" },
    { n: photos || 0,         te: "జ్ఞాపకాలు",       en: "Memories Shared" },
  ];
  return (
    <SceneShell tone="dark" testid="director-stats">
      <SparklesIcon className="text-[#FCF6BA] mb-3" size={40} />
      <p className="gold-text-soft font-script drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(32px, 5.6vw, 64px)" }}>
        {lang === "te" ? "రాజ్యం పెరుగుతోంది..." : "The Kingdom Is Growing..."}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10 max-w-5xl">
        {items.map((it, i) => (
          <div
            key={i}
            className="rounded-3xl px-6 py-7 text-center"
            style={{
              background: "rgba(11, 7, 38, 0.78)",
              border: "1.5px solid rgba(252,246,186,0.45)",
              boxShadow: "0 18px 42px rgba(0,0,0,0.55), inset 0 0 24px rgba(212,175,55,0.18)",
              backdropFilter: "blur(12px)",
            }}
          >
            <p className="gold-text-soft font-display font-bold leading-none drop-shadow" style={{ fontSize: "clamp(42px, 6vw, 78px)" }}>
              {it.n}
            </p>
            <p
              className="mt-2 font-display"
              style={{
                ...TE,
                color: "#FFE5C7",
                fontSize: "clamp(13px, 1.6vw, 18px)",
                letterSpacing: "0.06em",
                textShadow: "0 1px 2px rgba(0,0,0,0.6)",
              }}
            >
              {lang === "te" ? it.te : it.en}
            </p>
          </div>
        ))}
      </div>
    </SceneShell>
  );
}

/* ============================================================
   BUILDUP — mystery messages with castle backdrop
============================================================ */
export function BuildupScene({ lang }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const tm = setInterval(() => setIdx((i) => (i + 1) % BUILDUP_MESSAGES.length), 8000);
    return () => clearInterval(tm);
  }, []);
  const m = BUILDUP_MESSAGES[idx];
  return (
    <SceneShell tone="dark" backdrop testid="director-buildup">
      <Crown className="text-[#FCF6BA] mb-4 drop-shadow" size={48} />
      <p
        key={idx}
        className="font-script gold-text drop-shadow-2xl animate-[fadeUp_0.9s_ease-out] text-center px-6"
        style={{ ...TE, fontSize: "clamp(32px, 6.4vw, 72px)" }}
      >
        {lang === "te" ? m.te : m.en}
      </p>
    </SceneShell>
  );
}

/* ============================================================
   COUNTDOWN — fullscreen "5 minutes remaining" with roaming faces
============================================================ */
export function CountdownScene({ lang, minsLeft, guests }) {
  return (
    <SceneShell tone="dark" backdrop testid="director-countdown">
      {/* Roaming guest face bubbles — random anchored positions, each animated
          with one of three "roam" keyframes for organic wandering motion. */}
      {guests.slice(0, 16).map((g, i) => {
        const size = 78 + (i % 4) * 12;
        // Deterministic-but-spread positions so they don't clump.
        const left = 8 + ((i * 23) % 84);
        const top  = 12 + ((i * 41) % 70);
        const roam = ["roamA", "roamB", "roamC"][i % 3];
        const dur = 14 + (i % 6) * 2;
        return (
          <span
            key={g.id}
            className="absolute rounded-full overflow-hidden ring-2 ring-[#FCF6BA] shadow-2xl"
            style={{
              width: size, height: size,
              left: `${left}%`,
              top: `${top}%`,
              animation: `${roam} ${dur}s ease-in-out ${i * 0.3}s infinite alternate`,
              zIndex: 1,
              willChange: "transform",
            }}
          >
            <img src={fileUrl(g.character_path || g.selfie_path)} alt={g.name} className="w-full h-full object-cover" />
          </span>
        );
      })}
      <div className="relative z-10 text-center">
        <Cake size={56} className="mx-auto text-[#FFC4D6] mb-3" />
        <p className="label-eyebrow text-blush/85 mb-2" style={TE}>
          {lang === "te" ? "కేక్ కట్టింగ్ సమయంలోకి" : "Until Cake Cutting"}
        </p>
        <p
          className="gold-text font-display font-bold drop-shadow-2xl leading-none"
          style={{ fontSize: "clamp(140px, 26vw, 320px)" }}
        >
          {minsLeft}
        </p>
        <p className="font-script gold-text mt-3" style={{ ...TE, fontSize: "clamp(28px, 5vw, 56px)" }}>
          {lang === "te"
            ? (minsLeft === 1 ? "ఒక నిమిషం మిగిలింది" : `${minsLeft} నిమిషాలు మిగిలాయి`)
            : `${minsLeft} Minute${minsLeft === 1 ? "" : "s"} Remaining`}
        </p>
      </div>
    </SceneShell>
  );
}

/* ============================================================
   GRAND REVEAL — 5 cinematic scenes
   Removed "Our Beloved Guests" per spec. Stars is now a pure star sky.
   Messages is now "A Message From Anagha" (single big card).
============================================================ */
export function RevealScene({ lang, sceneId, guests, photos, info }) {
  switch (sceneId) {
    case "reveal-title":
      return (
        <SceneShell tone="dark" backdrop testid="director-reveal-title">
          <Crown className="text-[#FCF6BA] mb-4" size={70} />
          <p className="gold-text-soft font-script drop-shadow-2xl text-center" style={{ ...TE, fontSize: "clamp(48px, 8vw, 110px)" }}>
            {lang === "te" ? "యువరాణి అనఘ రాజ్యం" : "The Kingdom of Princess Anagha"}
          </p>
        </SceneShell>
      );
    case "reveal-stars":
      // Pure star sky — no title overlap, no labels, just twinkling stars
      // with guest faces tucked inside as star bodies (à la /gallery).
      return <PureStarSky guests={guests} />;
    case "reveal-memories":
      return <MemoryFloat photos={photos} lang={lang} />;
    case "reveal-messages":
      return <AnaghaMessage lang={lang} info={info} />;
    case "reveal-final":
      return <FinalScene lang={lang} guests={guests} info={info} />;
    default:
      return null;
  }
}

/* ----- Stars: pure starfield with embedded guest faces, no title ----- */
function PureStarSky({ guests }) {
  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="director-reveal-stars">
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, #1a0b4a 0%, #0c0830 45%, #02021a 100%)" }}
      />
      {/* moon halo top-right */}
      <div
        className="absolute"
        style={{
          width: "26vmin", height: "26vmin",
          right: "8vmin", top: "8vmin",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,236,170,0.6), rgba(255,236,170,0) 65%)",
          filter: "blur(2px)",
        }}
      />
      {/* dense twinkles */}
      {[...Array(140)].map((_, i) => (
        <span
          key={`t-${i}`}
          className="absolute rounded-full bg-white"
          style={{
            width: 1 + (i % 3),
            height: 1 + (i % 3),
            left: `${(i * 71) % 100}%`,
            top: `${(i * 53) % 100}%`,
            opacity: 0.5 + ((i % 5) * 0.1),
            animation: `twinkle ${2 + (i % 4)}s ease-in-out ${i * 0.07}s infinite alternate`,
          }}
        />
      ))}
      {/* guests as embedded stars */}
      {guests.slice(0, 48).map((g, i) => {
        const size = 36 + ((i * 11) % 20);
        const left = 6 + ((i * 23) % 88);
        const top  = 8  + ((i * 41) % 80);
        return (
          <span
            key={g.id}
            className="absolute rounded-full overflow-hidden"
            style={{
              width: size, height: size,
              left: `${left}%`, top: `${top}%`,
              boxShadow: "0 0 18px 4px rgba(252,246,186,0.55)",
              animation: `twinkle ${4 + (i % 5)}s ease-in-out ${i * 0.15}s infinite alternate`,
              zIndex: 2,
            }}
          >
            <img
              src={fileUrl(g.character_path || g.selfie_path)}
              alt={g.name}
              className="w-full h-full object-cover"
              style={{ filter: "brightness(1.05) saturate(1.05)" }}
            />
          </span>
        );
      })}
    </div>
  );
}

/* ----- Memories: floating polaroid-style cards with Ken Burns + tilt ----- */
function MemoryFloat({ photos, lang }) {
  const top = photos.slice(0, 6);
  return (
    <SceneShell tone="dark" backdrop testid="director-reveal-memories">
      <p className="gold-text-soft font-script mb-2 drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(30px, 5.4vw, 56px)" }}>
        {lang === "te" ? "అనఘ జ్ఞాపకాలు" : "Memories of Anagha"}
      </p>
      <p className="font-display text-blush/80 mb-6 italic" style={{ ...TE, fontSize: "clamp(13px, 1.8vw, 18px)" }}>
        {lang === "te" ? "ప్రతి క్షణం ఒక బంగారు జ్ఞాపకం ✦" : "Every moment a golden memory ✦"}
      </p>
      <div className="relative w-full max-w-6xl" style={{ height: "min(60vh, 560px)" }}>
        {top.map((p, i) => {
          // Spread polaroids in a loose collage
          const positions = [
            { l: "8%",  t: "5%",  rot: -6, dur: 9  },
            { l: "55%", t: "0%",  rot:  4, dur: 11 },
            { l: "30%", t: "30%", rot: -3, dur: 10 },
            { l: "70%", t: "35%", rot:  6, dur: 12 },
            { l: "5%",  t: "55%", rot:  3, dur: 9.5 },
            { l: "55%", t: "60%", rot: -5, dur: 11 },
          ];
          const pos = positions[i % positions.length];
          return (
            <div
              key={p.id}
              className="absolute"
              style={{
                left: pos.l, top: pos.t,
                width: "clamp(180px, 22vw, 320px)",
                transform: `rotate(${pos.rot}deg)`,
                animation: `polaroidFloat ${pos.dur}s ease-in-out ${i * 0.4}s infinite alternate`,
                zIndex: 6 - i,
              }}
            >
              <div
                className="bg-white p-3 pb-10 rounded-sm"
                style={{
                  boxShadow: "0 22px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.1)",
                }}
              >
                <div className="aspect-[4/3] overflow-hidden bg-black">
                  <img
                    src={fileUrl(p.path)}
                    alt=""
                    className="w-full h-full object-cover animate-ken-burns"
                  />
                </div>
                <p className="text-center font-script text-plum-deep mt-2" style={{ ...TE, fontSize: 18 }}>
                  ✦ Anagha ✦
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </SceneShell>
  );
}

/* ----- Anagha's Message: one big readable card with her portrait ----- */
function AnaghaMessage({ lang, info }) {
  const princess = "/anagha-dance.png";
  const message = lang === "te"
    ? "నా చిన్ని హృదయం నుండి మీకు ధన్యవాదాలు. మీరు ఇచ్చిన ప్రేమ, ఆశీర్వాదాలు, ఆనందం — ఇవే నా రాజ్యానికి అలంకారం. ఈ రోజు మీరు మాతో ఉన్నందుకు, నా మొదటి పుట్టినరోజును మరువలేని జ్ఞాపకంగా మార్చినందుకు మనస్సుపూర్వకంగా కృతజ్ఞతలు."
    : "From my tiny heart, thank you. Your love, your blessings and your joy are the jewels of my kingdom. Thank you for being with us today and turning my very first birthday into a memory I'll treasure forever.";

  return (
    <SceneShell tone="dark" backdrop testid="director-reveal-messages">
      <p className="gold-text-soft font-script mb-6 drop-shadow-2xl text-center" style={{ ...TE, fontSize: "clamp(30px, 5vw, 56px)" }}>
        {lang === "te" ? "అనఘ నుండి సందేశం" : "A Message From Anagha"}
      </p>
      <div
        className="relative max-w-4xl w-full mx-auto"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,244,220,0.96) 100%)",
          border: "2px solid #D4AF37",
          borderRadius: 28,
          padding: "clamp(28px, 4vw, 48px)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6), inset 0 0 40px rgba(212,175,55,0.18)",
        }}
      >
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div
            className="rounded-full overflow-hidden ring-4 ring-[#D4AF37] shrink-0"
            style={{ width: "clamp(110px, 14vw, 170px)", height: "clamp(110px, 14vw, 170px)" }}
          >
            <img src={princess} alt={info.princess_name || "Anagha"} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 text-left">
            <p
              className="font-display italic text-plum-deep leading-relaxed"
              style={{ ...TE, fontSize: "clamp(17px, 2.1vw, 26px)" }}
            >
              &ldquo;{message}&rdquo;
            </p>
            <p
              className="font-script text-plum mt-5 text-right"
              style={{ ...TE, fontSize: "clamp(22px, 3vw, 36px)" }}
            >
              — {lang === "te" ? "ప్రేమతో, యువరాణి అనఘ ✦" : `With Love, Princess ${info.princess_name || "Anagha"} ✦`}
            </p>
          </div>
        </div>
      </div>
    </SceneShell>
  );
}

function FinalScene({ lang, guests, info }) {
  const princess = "/anagha-dance.png";
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const tm = setTimeout(() => setPhase(1), 15000);
    return () => clearTimeout(tm);
  }, []);
  const messages = [
    {
      te: "నా మొదటి సంవత్సరం ప్రేమతో నింపిన అందరూ వీరే.",
      en: "These are the people who filled my first year with love.",
    },
    {
      te: "మా రాజ్యంలో ఉన్నందుకు ధన్యవాదాలు",
      en: "Thank You For Being Part Of My Kingdom",
    },
  ];
  const m = messages[phase];

  // To absolutely guarantee no overlap with the centered portrait + card, the
  // face bubbles only appear inside a narrow band along the BOTTOM ~25% of the
  // screen. This becomes a "row of friends" supporting Anagha visually.
  const faces = guests.slice(0, 14);

  return (
    <SceneShell tone="dark" backdrop testid="director-reveal-final">
      {/* Bottom band of guest faces — safe zone, never near the portrait/card */}
      <div className="absolute inset-x-0 bottom-3 z-[2] flex justify-center gap-3 sm:gap-5 px-4 flex-wrap">
        {faces.map((g, i) => (
          <span
            key={g.id}
            className="rounded-full overflow-hidden ring-2 ring-[#FCF6BA] shadow-2xl shrink-0"
            style={{
              width:  "clamp(48px, 7vmin, 72px)",
              height: "clamp(48px, 7vmin, 72px)",
              animation: `floatY ${5 + (i % 4)}s ease-in-out ${i * 0.18}s infinite`,
            }}
          >
            <img
              src={fileUrl(g.character_path || g.selfie_path)}
              alt={g.name}
              className="w-full h-full object-cover"
            />
          </span>
        ))}
      </div>

      {/* Centered portrait + dark glass message card — the safe zone */}
      <div className="relative z-10 flex flex-col items-center w-full px-6">
        <div
          className="rounded-full overflow-hidden ring-4 ring-[#FCF6BA] shadow-[0_0_80px_rgba(252,246,186,0.7)]"
          style={{ width: "min(26vmin, 240px)", height: "min(26vmin, 240px)" }}
        >
          <img src={princess} alt={info.princess_name || "Anagha"} className="w-full h-full object-cover" />
        </div>
        <div
          key={phase}
          className="mt-6 px-8 py-5 rounded-3xl animate-[fadeUp_1.2s_ease-out]"
          style={{
            maxWidth: "min(78vw, 880px)",
            background: "rgba(11, 7, 38, 0.78)",
            backdropFilter: "blur(14px)",
            border: "1.5px solid rgba(252,246,186,0.35)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
          }}
        >
          <p
            className="font-script gold-text-soft drop-shadow-2xl text-center"
            style={{ ...TE, fontSize: "clamp(24px, 3.8vw, 46px)", lineHeight: 1.25 }}
          >
            {lang === "te" ? m.te : m.en}
          </p>
        </div>
      </div>
    </SceneShell>
  );
}

/* ============================================================
   CAKE MODE — fireworks + floating guest photos, no chrome
============================================================ */
export function CakeModeScene({ lang, guests }) {
  return (
    <SceneShell tone="dark" backdrop testid="director-cake">
      {/* Fireworks */}
      {[...Array(20)].map((_, i) => (
        <span key={i}
          className="absolute rounded-full"
          style={{
            left: `${(i * 53) % 100}%`,
            top:  `${(i * 31) % 100}%`,
            width: 6, height: 6,
            background: "radial-gradient(circle, #FFE5A0, #FF6BA6 60%, transparent 70%)",
            boxShadow: "0 0 22px 8px rgba(255,180,80,0.55)",
            animation: `firework ${1.6 + (i % 5) * 0.3}s ease-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
      {guests.slice(0, 16).map((g, i) => (
        <span key={g.id}
          className="absolute rounded-full overflow-hidden ring-2 ring-[#FCF6BA]"
          style={{
            width: 90, height: 90,
            left: `${(i * 17) % 88 + 6}%`,
            top:  `${(i * 23) % 80 + 8}%`,
            animation: `floatY ${5 + (i % 4)}s ease-in-out ${i * 0.3}s infinite`,
          }}
        >
          <img src={fileUrl(g.character_path || g.selfie_path)} alt={g.name} className="w-full h-full object-cover" />
        </span>
      ))}
      <div className="relative z-10 text-center">
        <Cake className="text-[#FFC4D6] mx-auto mb-4" size={84} />
        <p className="gold-text font-script drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(44px, 7vw, 96px)" }}>
          {lang === "te" ? "శుభాకాంక్షలు, యువరాణి!" : "Happy Birthday, Princess!"}
        </p>
      </div>
    </SceneShell>
  );
}

/* ============================================================
   FAREWELL — Stats + final thank-you
============================================================ */
export function FarewellScene({ lang, stats, photos }) {
  const items = [
    { n: stats.total || 0,    te: "మంది అతిథులు",   en: "Guests Joined" },
    { n: stats.total || 0,    te: "నక్షత్రాలు",      en: "Stars Added" },
    { n: stats.messages || 0, te: "ఆశీర్వాదాలు",     en: "Blessings" },
    { n: photos || 0,         te: "ఫోటోలు",         en: "Photos Shared" },
  ];
  return (
    <SceneShell tone="dark" backdrop testid="director-farewell">
      <Heart className="text-[#FFC4D6] mb-3" size={42} fill="#FFC4D6" />
      <p className="gold-text-soft font-script drop-shadow-2xl text-center" style={{ ...TE, fontSize: "clamp(32px, 5vw, 64px)" }}>
        {lang === "te" ? "ధన్యవాదాలు" : "Thank You"}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-10 max-w-5xl">
        {items.map((it, i) => (
          <div
            key={i}
            className="rounded-3xl px-6 py-7 text-center"
            style={{
              background: "rgba(11, 7, 38, 0.78)",
              border: "1.5px solid rgba(252,246,186,0.45)",
              boxShadow: "0 18px 42px rgba(0,0,0,0.55), inset 0 0 24px rgba(212,175,55,0.18)",
              backdropFilter: "blur(12px)",
            }}
          >
            <p className="gold-text-soft font-display font-bold leading-none drop-shadow" style={{ fontSize: "clamp(38px, 5.6vw, 72px)" }}>
              {it.n}
            </p>
            <p
              className="mt-2 font-display"
              style={{
                ...TE,
                color: "#FFE5C7",
                fontSize: "clamp(13px, 1.6vw, 18px)",
                letterSpacing: "0.06em",
                textShadow: "0 1px 2px rgba(0,0,0,0.6)",
              }}
            >
              {lang === "te" ? it.te : it.en}
            </p>
          </div>
        ))}
      </div>
      <p className="font-script gold-text-soft drop-shadow-2xl text-center mt-10 px-6" style={{ ...TE, fontSize: "clamp(22px, 3.8vw, 40px)", maxWidth: "min(90vw, 1100px)" }}>
        {lang === "te"
          ? "మీ ప్రేమ వల్ల అనఘ మొదటి పుట్టినరోజు ఎప్పటికీ గుర్తుండిపోతుంది."
          : "Princess Anagha's First Birthday Will Always Be Remembered Because Of You."}
      </p>
    </SceneShell>
  );
}

/* ============================================================
   ARRIVAL ANNOUNCEMENT — 45s overlay (per spec)
   Rotating header + guest photo + name + relationship + role title.
============================================================ */
export function ArrivalAnnouncement({ lang, guest }) {
  const [headerIdx] = useState(() => Math.floor(Math.random() * ARRIVAL_HEADERS.length));
  if (!guest) return null;
  const header = ARRIVAL_HEADERS[headerIdx];
  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/85 backdrop-blur-md"
      data-testid="director-arrival"
    >
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background: "radial-gradient(circle at 50% 30%, rgba(212,175,55,0.25), transparent 60%)",
        }}
      />
      <Sparkles count={90} />
      <div className="relative z-10 text-center px-6 animate-[arrivalIn_0.7s_ease-out] max-w-3xl">
        <p className="font-script gold-text-soft drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(28px, 5vw, 52px)", lineHeight: 1.15 }}>
          {lang === "te" ? header.te : header.en}
        </p>
        <div
          className="mx-auto mt-7 rounded-full overflow-hidden ring-4 ring-[#FCF6BA] shadow-[0_0_70px_rgba(252,246,186,0.6)]"
          style={{ width: "min(36vmin, 320px)", height: "min(36vmin, 320px)" }}
        >
          <img
            src={fileUrl(guest.character_path || guest.selfie_path)}
            alt={guest.name}
            className="w-full h-full object-cover"
          />
        </div>
        <p className="font-script gold-text-soft mt-5 drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(34px, 6vw, 72px)", lineHeight: 1.05 }}>
          {guest.name}
        </p>
        <div className="mt-2 inline-flex items-center gap-2">
          <span className="w-8 h-px bg-[#D4AF37]" />
          <p className="font-display italic text-blush" style={{ ...TE, fontSize: "clamp(15px, 2.6vw, 22px)" }}>
            {guest.loving_title || guest.role_title}
          </p>
          <span className="w-8 h-px bg-[#D4AF37]" />
        </div>
        <p className="font-display text-blush/80 mt-1 capitalize" style={{ ...TE, fontSize: "clamp(13px, 2vw, 17px)" }}>
          {guest.relationship}
        </p>
      </div>
    </div>
  );
}

export { ARRIVAL_DURATION };

/* ============================================================
   KINGDOM STATUS PING — Phase 2 every 5 min
============================================================ */
export function KingdomStatusPing({ lang, stats }) {
  const messages = [
    { te: `${stats.total} మంది అతిథులు చేరారు`, en: `${stats.total} Guests Joined` },
    { te: `${stats.total} నక్షత్రాలు మెరుస్తున్నాయి`, en: `${stats.total} Stars Lit` },
    { te: `${stats.messages || 0} ఆశీర్వాదాలు అందాయి`, en: `${stats.messages || 0} Blessings Received` },
  ];
  const idx = (stats.total + (stats.messages || 0)) % messages.length;
  const m = messages[idx];
  return (
    <div
      className="fixed top-8 left-1/2 -translate-x-1/2 z-[70] glass-deep rounded-full px-6 py-3 shadow-2xl animate-[arrivalIn_0.5s_ease-out]"
      data-testid="director-status-ping"
    >
      <p className="font-script gold-text" style={{ ...TE, fontSize: 22 }}>
        ✨ {lang === "te" ? "రాజ్యం పెరుగుతోంది..." : "The Kingdom is Growing..."} · {lang === "te" ? m.te : m.en}
      </p>
    </div>
  );
}

/* ============================================================
   POST-EVENT
============================================================ */
export function PostEventScene({ lang }) {
  return (
    <SceneShell tone="dark" backdrop testid="director-post">
      <Heart className="text-[#FFC4D6] mb-4" size={48} fill="#FFC4D6" />
      <p className="gold-text font-script drop-shadow-2xl text-center" style={{ ...TE, fontSize: "clamp(36px, 5.6vw, 70px)" }}>
        {lang === "te" ? "మరోసారి ధన్యవాదాలు" : "Until Next Time"}
      </p>
      <p className="font-display text-blush mt-3" style={{ ...TE, fontSize: "clamp(16px, 2.6vw, 22px)" }}>
        {lang === "te" ? "మీ ప్రేమ మాతో ఎప్పటికీ ఉంటుంది." : "Your love will stay with us forever."}
      </p>
    </SceneShell>
  );
}

/* ============================================================
   MEMORIES PAIR — 2 photos at a time (per spec), rotates indefinitely
============================================================ */
export function MemoriesPairScene({ lang, photos, pageIdx = 0 }) {
  if (!photos || photos.length === 0) {
    return (
      <SceneShell tone="dark" backdrop testid="director-memories-pair">
        <p className="gold-text-soft font-script" style={{ ...TE, fontSize: "clamp(28px, 5vw, 52px)" }}>
          {lang === "te" ? "త్వరలో మరిన్ని జ్ఞాపకాలు" : "More memories coming soon"}
        </p>
      </SceneShell>
    );
  }
  // Pages of 2; wrap around
  const totalPages = Math.ceil(photos.length / 2);
  const page = ((pageIdx % totalPages) + totalPages) % totalPages;
  const pair = [photos[page * 2], photos[page * 2 + 1]].filter(Boolean);
  return (
    <SceneShell tone="dark" backdrop testid="director-memories-pair">
      <p className="gold-text-soft font-script mb-2 drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(28px, 4.8vw, 50px)" }}>
        {lang === "te" ? "అనఘ జ్ఞాపకాలు" : "Memories of Anagha"}
      </p>
      <p className="font-display text-blush/85 mb-6 italic" style={{ ...TE, fontSize: "clamp(13px, 1.8vw, 17px)" }}>
        ✦ {lang === "te" ? "ప్రతి క్షణం ఒక బంగారు ముత్యం" : "Every moment a golden pearl"} ✦
      </p>
      <div className="grid grid-cols-2 gap-6 w-full max-w-5xl">
        {pair.map((p, i) => (
          <div
            key={p.id}
            className="rounded-2xl overflow-hidden bg-black"
            style={{
              border: "3px solid #D4AF37",
              boxShadow: "0 18px 48px rgba(0,0,0,0.55), 0 0 0 8px rgba(255,255,255,0.06)",
              aspectRatio: "4/3",
              animation: `fadeUp 0.9s ease-out ${i * 0.2}s both`,
            }}
          >
            <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover animate-ken-burns" />
          </div>
        ))}
      </div>
      <p className="label-eyebrow text-blush/60 mt-6" style={TE}>
        {lang === "te" ? `${page + 1} / ${totalPages}` : `${page + 1} / ${totalPages}`}
      </p>
    </SceneShell>
  );
}

/* ============================================================
   CAKE PREP — anticipation buildup with rotating messages
============================================================ */
export function CakePrepScene({ lang }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const tm = setInterval(() => setIdx((i) => (i + 1) % CAKE_PREP_MESSAGES.length), 7000);
    return () => clearInterval(tm);
  }, []);
  const m = CAKE_PREP_MESSAGES[idx];
  return (
    <SceneShell tone="dark" backdrop testid="director-cake-prep">
      <Crown className="text-[#FCF6BA] mb-4 drop-shadow-2xl animate-pulse" size={56} />
      <p
        key={idx}
        className="font-script gold-text-soft drop-shadow-2xl animate-[fadeUp_1s_ease-out] text-center px-6"
        style={{ ...TE, fontSize: "clamp(30px, 6vw, 68px)", maxWidth: "min(90vw, 1100px)" }}
      >
        {lang === "te" ? m.te : m.en}
      </p>
      <Cake size={32} className="text-[#FFC4D6] mt-8 animate-pulse" />
    </SceneShell>
  );
}

/* ============================================================
   CAKE CELEBRATION — fireworks + floating photos + confetti
============================================================ */
export function CakeCelebrationScene({ lang, guests }) {
  return (
    <SceneShell tone="dark" backdrop testid="director-cake-celebrate">
      {/* Fireworks bursts */}
      {[...Array(28)].map((_, i) => (
        <span
          key={`fw-${i}`}
          className="absolute rounded-full"
          style={{
            left: `${(i * 53) % 96 + 2}%`,
            top:  `${(i * 31) % 88 + 4}%`,
            width: 8, height: 8,
            background: i % 3 === 0
              ? "radial-gradient(circle, #FFE5A0, #FF6BA6 60%, transparent 70%)"
              : i % 3 === 1
                ? "radial-gradient(circle, #FCF6BA, #D4AF37 60%, transparent 70%)"
                : "radial-gradient(circle, #FFC4D6, #C026D3 60%, transparent 70%)",
            boxShadow: "0 0 28px 10px rgba(255,180,80,0.5)",
            animation: `firework ${1.4 + (i % 5) * 0.3}s ease-out ${i * 0.18}s infinite`,
            zIndex: 1,
          }}
        />
      ))}
      {/* Confetti strips */}
      {[...Array(40)].map((_, i) => (
        <span
          key={`cf-${i}`}
          className="absolute"
          style={{
            left: `${(i * 27) % 100}%`,
            top: `-10%`,
            width: 6, height: 14,
            background: ["#FCF6BA", "#FFC4D6", "#D4AF37", "#9333EA", "#22D3EE"][i % 5],
            transform: `rotate(${(i * 23) % 360}deg)`,
            opacity: 0.85,
            animation: `confettiFall ${4 + (i % 5) * 0.4}s linear ${i * 0.1}s infinite`,
            zIndex: 1,
          }}
        />
      ))}
      {/* Floating guest photos around the castle */}
      {guests.slice(0, 18).map((g, i) => {
        const left = 6 + ((i * 19) % 88);
        const top  = 10 + ((i * 37) % 70);
        return (
          <span key={g.id}
            className="absolute rounded-full overflow-hidden ring-2 ring-[#FCF6BA] shadow-2xl"
            style={{
              width: 84, height: 84,
              left: `${left}%`, top: `${top}%`,
              animation: `floatY ${5 + (i % 4)}s ease-in-out ${i * 0.25}s infinite`,
              zIndex: 2,
            }}
          >
            <img src={fileUrl(g.character_path || g.selfie_path)} alt={g.name} className="w-full h-full object-cover" />
          </span>
        );
      })}
      <div className="relative z-10 text-center">
        <Cake className="text-[#FFC4D6] mx-auto mb-4 drop-shadow-2xl" size={96} />
        <p className="gold-text-soft font-script drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(46px, 7.4vw, 104px)" }}>
          {lang === "te" ? "శుభాకాంక్షలు, యువరాణి!" : "Happy Birthday, Princess!"}
        </p>
        <p className="font-display text-blush/95 mt-3" style={{ ...TE, fontSize: "clamp(16px, 2.6vw, 24px)" }}>
          {lang === "te" ? "మా చిట్టి తల్లి · లక్ష్మి అనఘ ధృతి" : "Our Chitti Thalli · Lakshmi Anagha Dhruthi"}
        </p>
      </div>
    </SceneShell>
  );
}

/* ============================================================
   DINNER MESSAGES — Plays all "Messages from Anagha" sequentially,
   then auto-transitions to the Memories of Anagha slideshow (looping).
============================================================ */
export function DinnerMessagesScene({ lang, info, guests }) {
  const messages = (guests || []).filter((g) => g.personal_message);
  // Fallback message if no guest has written one yet so the projector never
  // ends up blank when the host hits Start Dinner.
  const slides = messages.length > 0 ? messages : [{
    name: info.princess_name || "Anagha",
    loving_title: "Princess",
    personal_message: lang === "te"
      ? "మీ ప్రేమకు, ఆశీర్వాదాలకు హృదయపూర్వక ధన్యవాదాలు."
      : "Thank you with all my heart for your love and blessings.",
    character_path: null,
  }];

  // Stage 1: cycle through all messages once (15s each per host's request).
  // When done → Stage 2 (memories slideshow loops indefinitely).
  const [stage, setStage] = useState("messages"); // "messages" | "memories"
  const [idx, setIdx] = useState(0);
  const PER_MSG_SECS = 15;

  useEffect(() => {
    if (stage !== "messages") return;
    const tm = setTimeout(() => {
      if (idx + 1 < slides.length) {
        setIdx((i) => i + 1);
      } else {
        // All messages displayed — switch to memories slideshow indefinitely
        setStage("memories");
      }
    }, PER_MSG_SECS * 1000);
    return () => clearTimeout(tm);
  }, [stage, idx, slides.length]);

  // Stage 2: render the full memories slideshow inline — loops on its own
  if (stage === "memories") {
    return (
      <div className="absolute inset-0 bg-black" data-testid="director-dinner-memories">
        <Memories embed />
      </div>
    );
  }

  // Stage 1: one message at a time
  const slide = slides[idx % slides.length];
  return (
    <SceneShell tone="dark" backdrop testid="director-dinner">
      <p className="gold-text-soft font-script mb-6 drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(28px, 5vw, 52px)" }}>
        {lang === "te" ? "అనఘ నుండి సందేశాలు" : "Messages From Anagha"}
      </p>
      <div
        key={idx}
        className="max-w-3xl w-full mx-auto rounded-3xl text-center animate-[fadeUp_1s_ease-out]"
        style={{
          background: "linear-gradient(180deg, rgba(255,248,220,0.96) 0%, rgba(255,232,190,0.96) 100%)",
          border: "2px solid #D4AF37",
          padding: "clamp(24px, 4vw, 40px) clamp(28px, 5vw, 56px)",
          boxShadow: "0 22px 60px rgba(0,0,0,0.5), inset 0 0 30px rgba(212,175,55,0.18)",
        }}
      >
        <div className="flex items-center justify-center gap-4 mb-4">
          <img
            src={fileUrl(slide.character_path || slide.selfie_path) || "/chitti-thalli.png"}
            alt={slide.name}
            className="w-20 h-20 rounded-full object-cover ring-4 ring-[#D4AF37]"
            onError={(e) => { e.currentTarget.src = "/chitti-thalli.png"; }}
          />
          <div className="text-left">
            <p className="font-script gold-text" style={{ ...TE, fontSize: "clamp(24px, 3.4vw, 36px)" }}>{slide.name}</p>
            {(slide.loving_title || slide.role_title) && (
              <p className="font-display italic text-plum-deep" style={{ ...TE, fontSize: "clamp(14px, 2vw, 17px)" }}>
                {slide.loving_title || slide.role_title}
              </p>
            )}
          </div>
        </div>
        <p className="font-display italic text-plum-deep leading-relaxed" style={{ ...TE, fontSize: "clamp(17px, 2.4vw, 26px)" }}>
          &ldquo;{slide.personal_message}&rdquo;
        </p>
        <p className="font-script gold-text mt-4 text-right" style={{ ...TE, fontSize: "clamp(20px, 2.8vw, 30px)" }}>
          ✦ {lang === "te" ? "ప్రేమతో, అనఘ" : "With Love, Anagha"} ✦
        </p>
      </div>
      <p className="label-eyebrow text-blush/60 mt-6" style={TE}>
        {`${idx + 1} / ${slides.length}`} · {lang === "te" ? "తర్వాత: అనఘ జ్ఞాపకాలు" : "Next: Memories of Anagha"}
      </p>
    </SceneShell>
  );
}

/* ============================================================
   FINAL FAREWELL — 4-step cinematic with fade-out
============================================================ */
export function FinalFarewellScene({ lang, guests, info }) {
  const [step, setStep] = useState(0);
  // Step 0..3 then fade out (step 4)
  useEffect(() => {
    const timings = [6500, 6500, 7500, 7500];
    const tm = setTimeout(() => setStep((s) => Math.min(s + 1, 4)), timings[Math.min(step, 3)]);
    return () => clearTimeout(tm);
  }, [step]);

  const lines = [
    {
      te: "నా మొదటి సంవత్సరం ప్రేమతో నింపిన అందరూ వీరే.",
      en: "These Are The People Who Filled My First Year With Love.",
    },
    {
      te: "మా రాజ్యంలో ఉన్నందుకు ధన్యవాదాలు",
      en: "Thank You For Being Part Of My Kingdom",
    },
    {
      te: "మీ ప్రేమ వల్ల అనఘ మొదటి పుట్టినరోజు ఎప్పటికీ గుర్తుండిపోతుంది.",
      en: "Princess Anagha's First Birthday Will Always Be Remembered Because Of You.",
    },
    {
      te: "ప్రేమతో,\nరామ్, ప్రజ్వల & యువరాణి లక్ష్మి అనఘ ధృతి ❤️",
      en: "With Love,\nRam, Prajwala & Princess Lakshmi Anagha Dhruthi ❤️",
    },
  ];
  const fadedOut = step >= 4;
  const text = lines[Math.min(step, 3)];

  return (
    <SceneShell tone="dark" backdrop testid="director-final-farewell">
      {/* Fade overlay for the gentle fade-out */}
      <div
        className="absolute inset-0 z-[5] pointer-events-none transition-opacity"
        style={{ background: "#000", opacity: fadedOut ? 1 : 0, transitionDuration: "3000ms" }}
      />

      {/* Guest faces along bottom — safe band */}
      <div className="absolute inset-x-0 bottom-3 z-[2] flex justify-center gap-3 sm:gap-5 px-4 flex-wrap">
        {guests.slice(0, 16).map((g, i) => (
          <span
            key={g.id}
            className="rounded-full overflow-hidden ring-2 ring-[#FCF6BA] shadow-2xl shrink-0"
            style={{
              width: "clamp(48px, 7vmin, 72px)",
              height: "clamp(48px, 7vmin, 72px)",
              animation: `floatY ${5 + (i % 4)}s ease-in-out ${i * 0.18}s infinite`,
            }}
          >
            <img src={fileUrl(g.character_path || g.selfie_path)} alt={g.name} className="w-full h-full object-cover" />
          </span>
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center w-full px-6">
        <div
          className="rounded-full overflow-hidden ring-4 ring-[#FCF6BA] shadow-[0_0_80px_rgba(252,246,186,0.7)]"
          style={{ width: "min(26vmin, 240px)", height: "min(26vmin, 240px)" }}
        >
          <img src="/chitti-thalli.png" alt={info.princess_name || "Anagha"} className="w-full h-full object-cover" />
        </div>
        <div
          key={step}
          className="mt-6 px-8 py-5 rounded-3xl animate-[fadeUp_1.2s_ease-out]"
          style={{
            maxWidth: "min(82vw, 980px)",
            background: "rgba(11, 7, 38, 0.82)",
            backdropFilter: "blur(14px)",
            border: "1.5px solid rgba(252,246,186,0.35)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
          }}
        >
          <p
            className="font-script gold-text-soft drop-shadow-2xl text-center whitespace-pre-line"
            style={{ ...TE, fontSize: "clamp(24px, 4vw, 50px)", lineHeight: 1.3 }}
          >
            {lang === "te" ? text.te : text.en}
          </p>
        </div>
      </div>
    </SceneShell>
  );
}

/* ============================================================
   Shared wrapper — castle backdrop, sparkles, vignette
============================================================ */
function SceneShell({ children, backdrop = false, testid }) {
  return (
    <div className="absolute inset-0 overflow-hidden grid place-items-center" data-testid={testid}>
      <div
        className="absolute inset-0 animate-ken-burns"
        style={{
          backgroundImage: backdrop
            ? `url(${CASTLE_BG})`
            : "linear-gradient(180deg, #1d1252 0%, #0c0830 55%, #02021a 100%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.6) 100%)," +
            "linear-gradient(180deg, rgba(74,22,53,0.45), rgba(2,2,26,0.6))",
        }}
      />
      <Sparkles count={70} />
      <div className="relative z-10 flex flex-col items-center justify-center px-6 text-center text-blush w-full h-full">
        {children}
      </div>
    </div>
  );
}


/* ============================================================
   ANTICIPATION — "Let's see who is coming to my party!"
   Plays right after the doors open (~22s) before Sky of Stars.
============================================================ */
export function AnticipationScene({ lang }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const tm = setInterval(() => setIdx((i) => (i + 1) % ANTICIPATION_MESSAGES.length), 4200);
    return () => clearInterval(tm);
  }, []);
  const m = ANTICIPATION_MESSAGES[idx];

  return (
    <div
      className="absolute inset-0 overflow-hidden grid place-items-center"
      data-testid="director-anticipation"
      style={{
        background:
          "radial-gradient(ellipse at 50% 20%, #4a1654 0%, #1d0832 45%, #08041a 100%)",
      }}
    >
      <Sparkles count={90} />
      {/* Floating balloon field */}
      {[...Array(14)].map((_, i) => {
        const colors = ["#F0578F", "#FFD1E1", "#D4AF37", "#F493B6", "#B57315", "#7D3EB0"];
        const c = colors[i % colors.length];
        const left = (i * 71 + 5) % 92;
        const delay = (i * 0.7) % 6;
        const dur = 8 + (i % 5);
        return (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${left}%`,
              bottom: "-140px",
              animation: `balloonRise ${dur}s ease-in ${delay}s infinite`,
              opacity: 0.9,
            }}
            aria-hidden="true"
          >
            <div
              style={{
                width: 46, height: 58, borderRadius: "50% 50% 45% 45%",
                background: `radial-gradient(circle at 30% 30%, #fff8, ${c} 70%)`,
                boxShadow: `0 8px 24px ${c}66`,
              }}
            />
            <div style={{ width: 1, height: 60, background: "rgba(255,255,255,0.5)", margin: "0 auto" }} />
          </div>
        );
      })}
      {/* Center card */}
      <div className="relative z-10 text-center px-6 max-w-4xl" style={TE}>
        <div className="inline-flex items-center gap-2 mb-4">
          <Crown className="text-[#FCF6BA]" size={24} />
          <p className="label-eyebrow" style={{ color: "#D4AF37", letterSpacing: "0.28em" }}>
            {lang === "te" ? "నా పార్టీకి ఎవరు వస్తున్నారు?" : "WHO'S COMING TO MY PARTY?"}
          </p>
          <Crown className="text-[#FCF6BA]" size={24} />
        </div>
        <p
          key={idx}
          className="gold-text-soft drop-shadow-2xl"
          style={{
            fontFamily: "'Yatra One','Tiro Telugu','Noto Serif Telugu',serif",
            fontSize: "clamp(28px, 5.6vw, 68px)",
            lineHeight: 1.15,
            animation: "fadeUp 0.9s ease-out",
          }}
        >
          {lang === "te" ? m.te : m.en}
        </p>
        <p
          className="mt-6 text-blush/85 italic"
          style={{ fontSize: "clamp(14px, 2.4vw, 22px)" }}
        >
          {lang === "te"
            ? "కొన్ని క్షణాల్లో ఆకాశంలో ప్రతి అతిథి ఒక నక్షత్రంగా మెరుస్తారు..."
            : "In a few moments, every guest will shine as a star in the sky..."}
        </p>
      </div>
      <style>{`
        @keyframes balloonRise {
          0%   { transform: translateY(0) rotate(-3deg); opacity: 0; }
          10%  { opacity: 0.9; }
          100% { transform: translateY(-120vh) rotate(6deg); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}

/* ============================================================
   ARRIVAL BURST — balloons + fireworks that spawn when a new
   guest is announced. Layered ON TOP of the sky iframe.
============================================================ */
export function ArrivalBurst({ guest }) {
  return (
    <div className="absolute inset-0 pointer-events-none z-[85]" data-testid="arrival-burst">
      {/* Firework bursts at 6 random spots */}
      {[
        { x: 12, y: 22, c: "#F0578F" },
        { x: 78, y: 18, c: "#D4AF37" },
        { x: 30, y: 62, c: "#7D3EB0" },
        { x: 70, y: 68, c: "#FFE9A8" },
        { x: 52, y: 30, c: "#F493B6" },
        { x: 88, y: 55, c: "#FFF" },
      ].map((f, i) => (
        <span
          key={`${guest?.id || "g"}-fw-${i}`}
          className="absolute"
          style={{
            left: `${f.x}%`,
            top: `${f.y}%`,
            width: 12, height: 12, borderRadius: "50%",
            background: `radial-gradient(circle, ${f.c}, transparent 60%)`,
            boxShadow: `0 0 30px 12px ${f.c}66`,
            animation: `firework 1.6s ease-out ${i * 0.15}s 2`,
          }}
        />
      ))}
      {/* Rising balloons */}
      {[...Array(10)].map((_, i) => {
        const colors = ["#F0578F", "#FFD1E1", "#D4AF37", "#F493B6", "#7D3EB0"];
        const c = colors[i % colors.length];
        const left = (i * 83 + 10) % 92;
        return (
          <div
            key={`${guest?.id || "g"}-b-${i}`}
            className="absolute"
            style={{
              left: `${left}%`,
              bottom: "-140px",
              animation: `balloonRise ${5 + (i % 4)}s ease-in ${i * 0.2}s 1`,
              opacity: 0.95,
            }}
          >
            <div
              style={{
                width: 42, height: 54, borderRadius: "50% 50% 45% 45%",
                background: `radial-gradient(circle at 30% 30%, #fff9, ${c} 70%)`,
                boxShadow: `0 8px 24px ${c}66`,
              }}
            />
            <div style={{ width: 1, height: 60, background: "rgba(255,255,255,0.55)", margin: "0 auto" }} />
          </div>
        );
      })}
      {/* Confetti */}
      {[...Array(30)].map((_, i) => {
        const colors = ["#F0578F", "#FFE9A8", "#D4AF37", "#7D3EB0", "#F493B6"];
        return (
          <span
            key={`${guest?.id || "g"}-c-${i}`}
            className="absolute"
            style={{
              left: `${(i * 37) % 100}%`,
              top: "-20px",
              width: 6, height: 12,
              background: colors[i % colors.length],
              borderRadius: 2,
              animation: `confettiFall ${3 + (i % 4)}s linear ${i * 0.05}s 1`,
            }}
          />
        );
      })}
    </div>
  );
}
